import {
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes
} from "react";
import { cn } from "./cn";
import { Input as UiInput } from "./ui/input";
import { Textarea as UiTextarea } from "./ui/textarea";

const controlClassName =
  "w-full rounded-md border border-input bg-card px-3 py-2 text-sm text-foreground shadow-none outline-none transition-colors placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/20 disabled:cursor-not-allowed disabled:bg-muted disabled:opacity-60 aria-invalid:border-red-500 aria-invalid:ring-red-500/20";

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...props }, ref) {
    return <UiInput ref={ref} className={cn(controlClassName, "h-9", className)} {...props} />;
  }
);

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className, rows = 4, ...props }, ref) {
    return (
      <UiTextarea
        ref={ref}
        rows={rows}
        className={cn(controlClassName, "min-h-24 resize-y", className)}
        {...props}
      />
    );
  }
);

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function Select({ className, children, ...props }, ref) {
    return (
      <select ref={ref} className={cn(controlClassName, "h-9 py-1", className)} {...props}>
        {children}
      </select>
    );
  }
);

export interface FormFieldProps {
  label: string;
  children: ReactNode;
  htmlFor?: string;
  hint?: string;
  error?: string;
  optional?: boolean;
  className?: string;
}

export function FormField({
  label,
  children,
  htmlFor,
  hint,
  error,
  optional = false,
  className
}: FormFieldProps) {
  return (
    <div className={cn("space-y-1.5 text-left", className)}>
      <div className="flex items-baseline justify-between gap-3">
        {htmlFor ? (
          <label htmlFor={htmlFor} className="text-sm font-medium text-zinc-800 dark:text-zinc-200">
            {label}
          </label>
        ) : (
          <span className="text-sm font-medium text-zinc-800 dark:text-zinc-200">{label}</span>
        )}
        {optional ? <span className="text-xs text-zinc-500">Opcional</span> : null}
      </div>
      {children}
      {error ? (
        <p role="alert" className="text-xs text-red-600 dark:text-red-400">
          {error}
        </p>
      ) : hint ? (
        <p className="text-xs text-zinc-500 dark:text-zinc-400">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
